import { useMutation } from "@tanstack/react-query";
import usePrivateAxios from "@/common/api/usePrivateAxios";
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema";
import { unFormatCnpj, unFormatCurrency } from "@/common/utils/unFormatters";
import { PrivateCompanyLeadData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead";
import { transformNewGeos } from "@/common/utils/geoTransformers";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { transformNewProducts } from "@/common/utils/productTransformers";
import { getCleanPayload } from "@/common/utils/getCleanPayload";
import { getDirtyRailsUpdate } from "@/common/utils/getDirtyRailsUpdate";

interface UseUpdatePrivateCompanyLeadProps {
  id?: string
  privateCompanyLead?: PrivateCompanyLeadData
  onSuccess: () => void
  onError: (error: any) => void
}

const useUpdatePrivateCompanyLead = ({ id, privateCompanyLead, onSuccess, onError }: UseUpdatePrivateCompanyLeadProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updatePrivateCompanyLead'],
    mutationFn: (data: Partial<PrivateCompanyLeadsFunnelUpdateValidationSchema>) => {
      const payload = {
        ...data,
        financial_information_attributes: {
          ...data.financial_information_attributes,
          approved_credit: data?.financial_information_attributes?.approved_credit
            ? unFormatCurrency(data?.financial_information_attributes?.approved_credit)
            : undefined,
        },
        operational_information_attributes: {
          ...data.operational_information_attributes,
          expected_credit: data?.operational_information_attributes?.expected_credit
            ? unFormatCurrency(data?.operational_information_attributes?.expected_credit)
            : undefined,
          industry_segment_id: data?.operational_information_attributes?.industry_segment?.value || undefined,
          operational_information_product_categories_attributes: getDirtyRailsUpdate(
            privateCompanyLead?.operational_information?.product_categories || [],
            data?.operational_information_attributes?.product_categories,
            (x) => x.id,
            (x) => x.value,
            mapToDeletion,
            transformNewProducts
          ),
          geo_geoables_attributes: getDirtyRailsUpdate(
            privateCompanyLead?.operational_information?.geo_geoables || [],
            data?.operational_information_attributes?.geos,
            (geo) => geo.geo.id,
            (geo) => geo.value || '',
            mapToDeletion,
            transformNewGeos
          ),
        },
        fiscal_document_information_attributes: {
          ...data.fiscal_document_information,
          fiscal_document_kind: data.fiscal_document_information?.fiscal_document_kind?.value || undefined,
          seller_cnpj: data.fiscal_document_information?.seller_cnpj
            ? unFormatCnpj(data.fiscal_document_information.seller_cnpj)
            : undefined,

        },
        general_information_attributes: {
          ...data.general_information,
          company_logo: data.general_information?.new_company_logo,
        },
        address_attributes: {
          ...data.address_attributes,
          city_id: data.address_attributes?.city?.id,
        },
        printer_machine_id: data.printer_machine_id?.value || undefined,
        observations: data.observations || undefined,
      }

      delete payload.operational_information_attributes.geos;
      delete payload.contract;

      return privateAxios.putForm(`/funnel/private_company_leads/${id}`, {
        private_company_lead: getCleanPayload(payload),
      });
    },
    onSuccess,
    onError,
  });
}

export default useUpdatePrivateCompanyLead
